import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import IconButton from '@mui/material/IconButton';
import { useTenant } from '../../contexts/TenantContext';
import { canAccessSurveyManage } from '../../constants/surveyAccessPermissions';
import { SurveyRuntimeApiProvider, useSurveyRuntimeApi } from '../../surveys/context/SurveyRuntimeApiContext';
import { getApiErrorMessage } from '../../surveys/api/runtimeErrors';
import { useSurveyUiToast } from '../../surveys/hooks/useSurveyUiToast';
import QuestionGroupEditorDialog, { TYPE_LABELS } from '../../surveys/components/QuestionGroupEditorDialog';
import { Toast } from '../../surveys/components/SurveyToast';
import { FONT_FAMILY_PRIMARY } from '../../constants/fonts';

const PREVIEW_COUNT = 5;

function QuestionRow({ q, dense = false, onPurge }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: dense ? 0.4 : 0.6 }}>
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
        {q.label}
      </Typography>
      <Chip
        size="small"
        variant="outlined"
        label={TYPE_LABELS[q.type] || q.type}
        sx={{ fontSize: '0.68rem', height: 19, flexShrink: 0 }}
      />
      {onPurge && (
        <Tooltip title="Purge question — removes it from every group">
          <IconButton
            size="small"
            color="error"
            aria-label={`Purge question: ${q.label}`}
            onClick={(e) => {
              e.stopPropagation();
              onPurge(q);
            }}
            sx={{ p: 0.25, flexShrink: 0 }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

function GroupsPageInner() {
  const { hasPermission } = useTenant();
  const { apiFetch } = useSurveyRuntimeApi();
  const { showError } = useSurveyUiToast();

  const [toast, setToast] = useState(null);
  const [groups, setGroups] = useState(null);
  const [canCurate, setCanCurate] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [viewGroup, setViewGroup] = useState(null);
  const [addToGroup, setAddToGroup] = useState(null);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [purging, setPurging] = useState(false);
  const [purgeQuestion, setPurgeQuestion] = useState(null);
  const [purgingQuestion, setPurgingQuestion] = useState(false);

  const canView = canAccessSurveyManage(hasPermission);

  const load = useCallback(async () => {
    try {
      const [data, curate] = await Promise.all([
        apiFetch('/admin/question-groups'),
        apiFetch('/admin/question-bank/can-curate').catch(() => ({ can_curate: false })),
      ]);
      setGroups(data);
      setCanCurate(Boolean(curate?.can_curate));
    } catch (e) {
      setGroups([]);
      showError(getApiErrorMessage(e, 'Failed to load question groups.'));
    }
  }, [apiFetch, showError]);

  useEffect(() => {
    if (canView) void load();
    else setGroups([]);
  }, [canView, load]);

  // Keep the open "view more" dialog in sync after questions are added.
  function handleSaved(saved) {
    void load();
    if (saved?.id) {
      setViewGroup((cur) => (cur && cur.id === saved.id ? saved : cur));
      setToast({ key: Date.now(), message: 'Question group saved.' });
    }
  }

  async function purge() {
    if (!purgeTarget) return;
    setPurging(true);
    try {
      await apiFetch(`/admin/question-groups/${purgeTarget.id}/purge`, { method: 'POST' });
      setPurgeTarget(null);
      setViewGroup((cur) => (cur && cur.id === purgeTarget.id ? null : cur));
      setToast({ key: Date.now(), message: 'Group purged. Its questions remain available in the bank.' });
      void load();
    } catch (e) {
      showError(getApiErrorMessage(e, 'Failed to purge the group.'));
    } finally {
      setPurging(false);
    }
  }

  async function doPurgeQuestion() {
    if (!purgeQuestion) return;
    setPurgingQuestion(true);
    try {
      const r = await apiFetch(`/admin/question-bank/${purgeQuestion.id}/purge`, { method: 'POST' });
      const groups_n = r?.removed_from_groups ?? 0;
      const surveys_n = r?.still_used_by_surveys ?? 0;
      setPurgeQuestion(null);
      setToast({
        key: Date.now(),
        message:
          `Question purged — removed from ${groups_n} group${groups_n === 1 ? '' : 's'}.` +
          (surveys_n > 0
            ? ` ${surveys_n} survey${surveys_n === 1 ? '' : 's'} still display it.`
            : ''),
      });
      const fresh = await apiFetch('/admin/question-groups');
      setGroups(fresh);
      // Keep the open dialog in sync with the purge.
      setViewGroup((cur) => (cur ? fresh.find((x) => x.id === cur.id) ?? null : cur));
    } catch (e) {
      showError(getApiErrorMessage(e, 'Failed to purge the question.'));
    } finally {
      setPurgingQuestion(false);
    }
  }

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ fontFamily: FONT_FAMILY_PRIMARY, mb: 1 }}>
          Question groups
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You need survey authoring access to view question groups.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {toast && (
        <Toast
          key={toast.key}
          message={toast.message}
          variant="success"
          duration={2500}
          onDismiss={() => setToast(null)}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontFamily: FONT_FAMILY_PRIMARY, fontWeight: 600 }}>
            Question groups
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '70ch' }}>
            Reusable question templates shared across every client. Pick a group when building a
            survey to add all of its questions at once.
            {canCurate
              ? ' Questions and groups are never edited — create new ones, or purge what you no longer need.'
              : ' Groups are curated by the JLL master team; you can use them in any survey.'}
          </Typography>
        </Box>
        {canCurate && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{
              flexShrink: 0,
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '999px',
              backgroundColor: '#000000',
              '&:hover': { backgroundColor: '#333333' },
            }}
          >
            New group
          </Button>
        )}
      </Box>

      {groups === null ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading question groups…
          </Typography>
        </Box>
      ) : groups.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: '12px' }}>
          <QuizOutlinedIcon sx={{ fontSize: 56, color: 'action.disabled', mb: 1.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No question groups yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Group related questions into a template so surveys can reuse them in one click.
          </Typography>
          {canCurate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              sx={{
                textTransform: 'none',
                backgroundColor: '#000000',
                '&:hover': { backgroundColor: '#333333' },
              }}
            >
              New group
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={2.5}>
          {groups.map((g) => {
            const preview = g.questions.slice(0, PREVIEW_COUNT);
            const more = g.question_count - preview.length;
            return (
              <Grid item xs={12} sm={6} lg={4} key={g.id}>
                <Card
                  variant="outlined"
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: '12px' }}
                >
                  <CardContent sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                      {g.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {g.question_count} question{g.question_count === 1 ? '' : 's'}
                      {g.created_by_email ? ` · created by ${g.created_by_email}` : ''}
                    </Typography>
                    {g.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {g.description}
                      </Typography>
                    )}
                    <Divider sx={{ my: 1.5 }} />
                    {preview.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No questions in this group yet.
                      </Typography>
                    ) : (
                      preview.map((q) => (
                        <QuestionRow
                          key={q.id}
                          q={q}
                          onPurge={canCurate ? setPurgeQuestion : undefined}
                        />
                      ))
                    )}
                    {more > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        + {more} more question{more === 1 ? '' : 's'}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions sx={{ px: 2, pb: 1.5, pt: 0, gap: 0.5 }}>
                    <Button size="small" onClick={() => setViewGroup(g)} sx={{ textTransform: 'none' }}>
                      View more
                    </Button>
                    {canCurate && (
                      <>
                        <Button
                          size="small"
                          startIcon={<AddIcon fontSize="small" />}
                          onClick={() => setAddToGroup(g)}
                          sx={{ textTransform: 'none' }}
                        >
                          Add questions
                        </Button>
                        <Tooltip title="Purge group (questions are kept)">
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteOutlineIcon fontSize="small" />}
                            onClick={() => setPurgeTarget(g)}
                            sx={{ textTransform: 'none', ml: 'auto' }}
                          >
                            Purge
                          </Button>
                        </Tooltip>
                      </>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* View more — full question list */}
      <Dialog open={Boolean(viewGroup)} onClose={() => setViewGroup(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>{viewGroup?.name}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {viewGroup?.question_count} question{viewGroup?.question_count === 1 ? '' : 's'}
            {viewGroup?.created_by_email ? ` · created by ${viewGroup.created_by_email}` : ''}
            {viewGroup?.updated_by_email ? ` · last updated by ${viewGroup.updated_by_email}` : ''}
          </Typography>
          {viewGroup?.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {viewGroup.description}
            </Typography>
          )}
          {(viewGroup?.questions ?? []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No questions in this group yet.
            </Typography>
          ) : (
            viewGroup.questions.map((q) => (
              <Box key={q.id}>
                <QuestionRow q={q} dense onPurge={canCurate ? setPurgeQuestion : undefined} />
                {q.options?.length > 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', pl: 1, pb: 0.6 }}
                  >
                    {q.options.map((o) => o.label).join(' · ')}
                  </Typography>
                )}
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          {canCurate && viewGroup && (
            <Button
              startIcon={<AddIcon />}
              onClick={() => setAddToGroup(viewGroup)}
              sx={{ textTransform: 'none', mr: 'auto' }}
            >
              Add questions
            </Button>
          )}
          <Button onClick={() => setViewGroup(null)} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Purge a single question */}
      <Dialog
        open={Boolean(purgeQuestion)}
        onClose={() => !purgingQuestion && setPurgeQuestion(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Purge this question?</DialogTitle>
        <DialogContent>
          <Paper variant="outlined" sx={{ p: 1.25, mb: 1.5, borderRadius: '8px' }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {purgeQuestion?.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {TYPE_LABELS[purgeQuestion?.type] || purgeQuestion?.type}
            </Typography>
          </Paper>
          <Typography variant="body2" color="text.secondary">
            The question is removed from <strong>every group</strong> that contains it and can no
            longer be added to new surveys. Surveys that already use it — live, scheduled or closed —
            keep displaying it, because it is only removed from the groups. The record is retained
            for the dev team to remove permanently.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPurgeQuestion(null)}
            disabled={purgingQuestion}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void doPurgeQuestion()}
            disabled={purgingQuestion}
            sx={{ textTransform: 'none' }}
          >
            {purgingQuestion ? 'Purging…' : 'Purge question'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Purge confirmation */}
      <Dialog open={Boolean(purgeTarget)} onClose={() => !purging && setPurgeTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Purge “{purgeTarget?.name}”?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            The group stops appearing anywhere in the app. Its questions are not deleted — they stay
            in the question bank and in any other group. The record is retained for the dev team to
            remove permanently.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPurgeTarget(null)} disabled={purging} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void purge()}
            disabled={purging}
            sx={{ textTransform: 'none' }}
          >
            {purging ? 'Purging…' : 'Purge group'}
          </Button>
        </DialogActions>
      </Dialog>

      <QuestionGroupEditorDialog
        open={createOpen}
        mode="create"
        onClose={() => setCreateOpen(false)}
        onSaved={handleSaved}
      />
      <QuestionGroupEditorDialog
        open={Boolean(addToGroup)}
        mode="add"
        group={addToGroup}
        onClose={() => setAddToGroup(null)}
        onSaved={handleSaved}
      />
    </Box>
  );
}

export default function GroupsPage() {
  // The groups API lives on the survey runtime service.
  return (
    <SurveyRuntimeApiProvider>
      <GroupsPageInner />
    </SurveyRuntimeApiProvider>
  );
}
